/**
 * Related Products Carousel Component
 * Handles horizontal scrolling carousel for related products
 */

class RelatedProducts {
  constructor(container) {
    this.container = container;
    this.carousel = null;
    this.items = [];
    this.prevButton = null;
    this.nextButton = null;
    this.currentIndex = 0;
    this.itemsPerView = 4;
    this.scrollAmount = 0;
    
    this.init();
  }

  init() {
    this.cacheElements();
    this.calculateItemsPerView();
    this.bindEvents();
    this.updateNavigation();
  }

  cacheElements() {
    this.carousel = this.container.querySelector('.related-products__container');
    this.items = Array.from(this.container.querySelectorAll('.related-products__item'));
    this.prevButton = this.container.querySelector('.related-products__arrow--prev');
    this.nextButton = this.container.querySelector('.related-products__arrow--next');
  }

  calculateItemsPerView() {
    const containerWidth = this.container.offsetWidth;
    const itemWidth = 200; // Based on CSS flex: 0 0 200px
    const gap = 16; // 1rem gap
    
    this.itemsPerView = Math.floor((containerWidth + gap) / (itemWidth + gap));
    this.scrollAmount = itemWidth + gap;
  }

  bindEvents() {
    // Arrow navigation
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        this.previousSlide();
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        this.nextSlide();
      });
    }

    // Touch/swipe support
    this.addTouchSupport();

    // Keyboard navigation
    this.container.addEventListener('keydown', (e) => {
      this.handleKeydown(e);
    });

    // Resize handler
    window.addEventListener('resize', () => {
      this.handleResize();
    });

    // Add to cart buttons
    this.bindAddToCartEvents();
  }

  addTouchSupport() {
    let startX = 0;
    let startY = 0;
    let endX = 0;
    let endY = 0;
    let isScrolling = false;

    this.carousel.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isScrolling = false;
    });

    this.carousel.addEventListener('touchmove', (e) => {
      if (!isScrolling) {
        endX = e.touches[0].clientX;
        endY = e.touches[0].clientY;
        
        const deltaX = Math.abs(endX - startX);
        const deltaY = Math.abs(endY - startY);
        
        // Determine if this is a horizontal scroll
        if (deltaX > deltaY && deltaX > 10) {
          isScrolling = true;
        }
      }
    });

    this.carousel.addEventListener('touchend', (e) => {
      if (isScrolling) {
        endX = e.changedTouches[0].clientX;
        endY = e.changedTouches[0].clientY;
        
        const deltaX = endX - startX;
        const deltaY = endY - startY;
        
        // Only trigger if horizontal swipe is more significant than vertical
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
          if (deltaX > 0) {
            this.previousSlide();
          } else {
            this.nextSlide();
          }
        }
      }
    });
  }

  bindAddToCartEvents() {
    const addButtons = this.container.querySelectorAll('.related-product__add-btn');
    
    addButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleAddToCart(button);
      });
    });
  }

  handleAddToCart(button) {
    const productItem = button.closest('.related-product');
    const productTitle = productItem.querySelector('.related-product__title').textContent;
    
    // Add loading state
    const originalText = button.textContent;
    button.textContent = 'Adding...';
    button.disabled = true;
    
    // Simulate add to cart (replace with actual implementation)
    setTimeout(() => {
      button.textContent = 'Added!';
      button.style.backgroundColor = '#51cf66';
      
      // Show success message (optional)
      this.showSuccessMessage(`${productTitle} added to cart!`);
      
      // Reset button after delay
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
        button.style.backgroundColor = '';
      }, 2000);
    }, 1000);
  }

  showSuccessMessage(message) {
    // Create or update success message
    let messageEl = document.querySelector('.related-products__message');
    
    if (!messageEl) {
      messageEl = document.createElement('div');
      messageEl.className = 'related-products__message';
      this.container.appendChild(messageEl);
    }
    
    messageEl.textContent = message;
    messageEl.style.display = 'block';
    
    // Hide message after delay
    setTimeout(() => {
      messageEl.style.display = 'none';
    }, 3000);
  }

  nextSlide() {
    const maxIndex = Math.max(0, this.items.length - this.itemsPerView);
    
    if (this.currentIndex < maxIndex) {
      this.currentIndex++;
      this.scrollToIndex(this.currentIndex);
    }
  }

  previousSlide() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.scrollToIndex(this.currentIndex);
    }
  }

  scrollToIndex(index) {
    const scrollPosition = index * this.scrollAmount;
    
    this.carousel.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
    
    this.updateNavigation();
  }

  updateNavigation() {
    const maxIndex = Math.max(0, this.items.length - this.itemsPerView);
    
    if (this.prevButton) {
      this.prevButton.disabled = this.currentIndex === 0;
      this.prevButton.style.opacity = this.currentIndex === 0 ? '0.5' : '1';
    }
    
    if (this.nextButton) {
      this.nextButton.disabled = this.currentIndex >= maxIndex;
      this.nextButton.style.opacity = this.currentIndex >= maxIndex ? '0.5' : '1';
    }
  }

  handleKeydown(event) {
    const { key } = event;
    
    switch (key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.previousSlide();
        break;
        
      case 'ArrowRight':
        event.preventDefault();
        this.nextSlide();
        break;
    }
  }

  handleResize() {
    this.calculateItemsPerView();
    this.updateNavigation();
  }

  // Public methods
  goToSlide(index) {
    const maxIndex = Math.max(0, this.items.length - this.itemsPerView);
    const targetIndex = Math.max(0, Math.min(index, maxIndex));
    
    this.currentIndex = targetIndex;
    this.scrollToIndex(this.currentIndex);
  }

  getCurrentIndex() {
    return this.currentIndex;
  }

  getTotalSlides() {
    return Math.max(1, this.items.length - this.itemsPerView + 1);
  }

  // Auto-scroll functionality (optional)
  startAutoScroll(interval = 5000) {
    this.stopAutoScroll();
    this.autoScrollInterval = setInterval(() => {
      const maxIndex = Math.max(0, this.items.length - this.itemsPerView);
      
      if (this.currentIndex >= maxIndex) {
        this.currentIndex = 0;
      } else {
        this.currentIndex++;
      }
      
      this.scrollToIndex(this.currentIndex);
    }, interval);
  }

  stopAutoScroll() {
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
      this.autoScrollInterval = null;
    }
  }

  // Pause auto-scroll on hover
  pauseOnHover() {
    this.container.addEventListener('mouseenter', () => {
      this.stopAutoScroll();
    });

    this.container.addEventListener('mouseleave', () => {
      // Only restart if auto-scroll was previously active
      if (this.autoScrollInterval === null) {
        this.startAutoScroll();
      }
    });
  }
}

// Initialize all related products carousels on the page
document.addEventListener('DOMContentLoaded', () => {
  const relatedProductsContainers = document.querySelectorAll('.related-products');
  
  relatedProductsContainers.forEach(container => {
    new RelatedProducts(container);
  });
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = RelatedProducts;
}
